import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Users, Play, Pause, RotateCcw, Crown, Skull, Heart, Swords, Wifi, WifiOff, Video, Camera, Activity, Circle, X, Dices, FlipHorizontal2, History, Sun, Moon, Shield, Sparkles, Zap, Gem, ChevronRight, ChevronLeft, Timer, Shuffle as ShuffleIcon, Link2, UserPlus, UserMinus, Copy, Check, Settings, Grid, MoreVertical } from 'lucide-react';
import { PlayerContainer } from './PlayerContainer';

import { LayoutSelector, useLayoutHotkeys, getGridLayoutClass, ViewMode, FormationPicker, getDefaultFormation, getFormationById, FORMATIONS, type Formation } from './LayoutSelector';
import { useMediaManager } from '../lib/webrtc-media';
import { supabase } from '../lib/supabase';

// Session State Machine
type AppState = 'LANDING' | 'IN_SESSION';
type SessionStatus = 'LOBBY' | 'ACTIVE' | 'PAUSED' | 'ENDED';

// Connection types
type ConnectionType = 'local' | 'online';

// ViewMode is now imported from LayoutSelector

// Player means - how each player interacts
// Local: lifeTracker | playtester  |  Online: webcam | playtester
type PlayerMeans = 'lifeTracker' | 'playtester' | 'webcam';

// Available means based on environment
const MEANS_BY_ENV: Record<ConnectionType, PlayerMeans[]> = {
    local: ['lifeTracker', 'playtester', 'webcam'],
    online: ['webcam', 'playtester'],
};

const MEANS_META: Record<PlayerMeans, { label: string; description: string; icon: string }> = {
    lifeTracker: { label: 'Life Tracker', description: 'Track physical cards — giant life display', icon: '❤️' },
    playtester: { label: 'Playtester', description: 'Digital deck sandbox — full card manipulation', icon: '🃏' },
    webcam: { label: 'Webcam', description: 'Stream your physical board via camera', icon: '📹' },
};

// Player feature toggles (secondary to means)
interface PlayerFeatures {
    hasMic: boolean;       // voice chat (online only)
    handRevealed: boolean; // whether hand is visible to opponents (playtester only)
}

// Player state
interface Player {
    id: string;
    name: string;
    seatIndex: number;
    life: number;
    poison: number;
    commanderTax: number;
    commanderDamage: number[];
    status: 'active' | 'eliminated';
    isReady: boolean;
    hasRolled: boolean;
    rollValue: number | null;
    // Per-player means selection
    means: PlayerMeans;
    // Feature toggles (secondary)
    features: PlayerFeatures;
    lastActiveAt: number;
    // Extra counters
    experience: number;
    energy: number;
    treasure: number;
    // Commander info
    commanderName: string;
    commanderArt: string; // Scryfall art_crop URL
    // Playtester deck data
    deck?: any[];
    deckName?: string;
}

// World state tracking
interface WorldState {
    monarch: string | null;      // player ID who holds monarch
    initiative: string | null;   // player ID who holds initiative
    dayNight: 'none' | 'day' | 'night';
    cityBlessing: Set<string>;   // player IDs who have city's blessing
}

// MTG Turn Phases
type GamePhase = 'untap' | 'upkeep' | 'draw' | 'main1' | 'combat' | 'main2' | 'end';
const PHASES: { id: GamePhase; label: string; short: string }[] = [
    { id: 'untap', label: 'Untap', short: 'UNT' },
    { id: 'upkeep', label: 'Upkeep', short: 'UPK' },
    { id: 'draw', label: 'Draw', short: 'DRW' },
    { id: 'main1', label: 'Main 1', short: 'M1' },
    { id: 'combat', label: 'Combat', short: 'CMB' },
    { id: 'main2', label: 'Main 2', short: 'M2' },
    { id: 'end', label: 'End Step', short: 'END' },
];

// Format presets
const FORMAT_PRESETS = {
    'commander': { name: 'Commander', startingLife: 40, commanderDamage: true },
    'standard': { name: 'Standard', startingLife: 20, commanderDamage: false },
    'modern': { name: 'Modern', startingLife: 20, commanderDamage: false },
    'legacy': { name: 'Legacy', startingLife: 20, commanderDamage: false },
    'two-headed': { name: '2-Headed Giant', startingLife: 30, commanderDamage: false },
    'brawl': { name: 'Brawl', startingLife: 25, commanderDamage: true },
};

// Feature descriptions
const FEATURE_INFO = {
    camera: { name: 'Camera', icon: Camera, description: 'Stream your play area', onlineOnly: true },
    mic: { name: 'Microphone', icon: Activity, description: 'Voice chat with players', onlineOnly: true },
    playtester: { name: 'Playtester', icon: Activity, description: 'Digital deck sandbox', onlineOnly: false },
};

// Dice types and helpers
type DieType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20' | 'coin';
const DIE_MAX: Record<DieType, number> = { d4: 4, d6: 6, d8: 8, d10: 10, d12: 12, d20: 20, coin: 2 };
const DIE_LABELS: Record<DieType, string> = { d4: 'd4', d6: 'd6', d8: 'd8', d10: 'd10', d12: 'd12', d20: 'd20', coin: '🪙' };

interface DiceRoll {
    die: DieType;
    result: number;
    label: string;
    timestamp: number;
}

// localStorage keys
const SESSION_STORAGE_KEY = 'deckstr_session_state';
const PLAYER_PROFILES_KEY = 'deckstr_player_profiles';

// Player profile (saved to localStorage)
interface PlayerProfile {
    name: string;
    preferredMeans: PlayerMeans;
    commanderName: string;
    commanderArt: string;
}

// Timer duration options
const TIMER_OPTIONS = [
    { value: 0, label: 'Off' },
    { value: 60, label: '1 min' },
    { value: 120, label: '2 min' },
    { value: 180, label: '3 min' },
    { value: 300, label: '5 min' },
    { value: 600, label: '10 min' },
];

export function SessionTab() {
    // FSM State
    const [appState, setAppState] = useState<AppState>('LANDING');
    const [sessionStatus, setSessionStatus] = useState<SessionStatus>('LOBBY');

    // Session config
    const [format, setFormat] = useState<keyof typeof FORMAT_PRESETS>('commander');
    const [maxPlayers, setMaxPlayers] = useState(4);
    const [rumbleMode, setRumbleMode] = useState(false);
    const [formation, setFormation] = useState<string>('4A');

    // Connection options
    const [connectionType, setConnectionType] = useState<ConnectionType>('local');
    const [recordSession, setRecordSession] = useState(false);

    // P2P State
    const [roomCode, setRoomCode] = useState<string>('');
    const [joinRoomCode, setJoinRoomCode] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectedPeers, setConnectedPeers] = useState(0);
    const [sessionId] = useState(() => crypto.randomUUID());

    // Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [recordingConsent, setRecordingConsent] = useState<Map<string, boolean>>(new Map());
    const [showConsentPrompt, setShowConsentPrompt] = useState(false);

    // UI State
    const [showHostModal, setShowHostModal] = useState(false);
    const [isPublicSession, setIsPublicSession] = useState(true);
    const [gamesTotal, setGamesTotal] = useState(1);
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [inviteCopied, setInviteCopied] = useState(false);

    // Dice Roller State
    const [showDiceRoller, setShowDiceRoller] = useState(false);
    const [diceHistory, setDiceHistory] = useState<DiceRoll[]>([]);
    const [isRolling, setIsRolling] = useState(false);
    const [lastRoll, setLastRoll] = useState<DiceRoll | null>(null);
    const [focusedSeat, setFocusedSeat] = useState<number | null>(null);
    const [showWorldState, setShowWorldState] = useState(false);
    const [showSessionMenu, setShowSessionMenu] = useState(false);

    // Join Session State with means selection
    const [showJoinModeModal, setShowJoinModeModal] = useState(false);
    const [joinPlayerName, setJoinPlayerName] = useState('');
    const [joinMeans, setJoinMeans] = useState<PlayerMeans>('lifeTracker');
    const [joinMic, setJoinMic] = useState(false);
    const [pendingJoinCode, setPendingJoinCode] = useState('');

    // Session container ref for recording
    const sessionContainerRef = useRef<HTMLDivElement>(null);

    // Media manager for webcam/mic (session-level, single camera for local user)
    const media = useMediaManager();

    // Which seat is the local player (for webcam pass-through)
    const [localPlayerSeat, setLocalPlayerSeat] = useState<number>(0);

    // Camera helpers
    const handleRequestCamera = useCallback(async () => {
        await media.requestStream();
    }, [media.requestStream]);

    const handleSwitchCamera = useCallback(async () => {
        const devices = media.devices.videoInputs;
        if (devices.length <= 1) return;
        const currentId = media.settings.videoDeviceId;
        const currentIdx = devices.findIndex(d => d.deviceId === currentId);
        const nextIdx = (currentIdx + 1) % devices.length;
        await media.setVideoDevice(devices[nextIdx].deviceId);
    }, [media.devices.videoInputs, media.settings.videoDeviceId, media.setVideoDevice]);

    // Default feature set for new players
    const defaultFeatures: PlayerFeatures = {
        hasMic: false,
        handRevealed: false,
    };

    // World state
    const [worldState, setWorldState] = useState<WorldState>({
        monarch: null,
        initiative: null,
        dayNight: 'none',
        cityBlessing: new Set(),
    });

    // Phase stepper
    const [currentPhase, setCurrentPhase] = useState<GamePhase>('untap');

    // Turn timer
    const [turnTimerDuration, setTurnTimerDuration] = useState(0); // 0 = off, seconds
    const [turnTimeRemaining, setTurnTimeRemaining] = useState(0);
    const [timerActive, setTimerActive] = useState(false);

    // Local player (for solo/local testing)
    const [players, setPlayers] = useState<Player[]>([
        {
            id: '1',
            name: 'Player 1',
            seatIndex: 0,
            life: 40,
            poison: 0,
            commanderTax: 0,
            commanderDamage: [0, 0, 0, 0, 0, 0, 0, 0],
            status: 'active',
            isReady: false,
            hasRolled: false,
            rollValue: null,
            means: 'lifeTracker',
            features: defaultFeatures,
            lastActiveAt: Date.now(),
            experience: 0,
            energy: 0,
            treasure: 0,
            commanderName: '',
            commanderArt: '',
        }
    ]);


    // Current turn tracking (must be before saveSessionState)
    const [currentTurn, setCurrentTurn] = useState(0);

    // P2P Room Handling (to be connected to sessionP2P manager)

    // ====== CRASH PROTECTION: Persist session state to localStorage ======
    const saveSessionState = useCallback(() => {
        if (appState !== 'IN_SESSION') return;
        try {
            const stateToSave = {
                appState,
                sessionStatus,
                format,
                maxPlayers,
                rumbleMode,
                formation,
                connectionType,
                roomCode,
                currentTurn,
                players: players.map(p => ({
                    id: p.id,
                    name: p.name,
                    seatIndex: p.seatIndex,
                    life: p.life,
                    poison: p.poison,
                    commanderTax: p.commanderTax,
                    commanderDamage: p.commanderDamage,
                    status: p.status,
                    isReady: p.isReady,
                    hasRolled: p.hasRolled,
                    rollValue: p.rollValue,
                    features: p.features,
                    lastActiveAt: p.lastActiveAt,
                })),
                savedAt: Date.now(),
            };
            localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (err) {
            console.error('Failed to save session state:', err);
        }
    }, [appState, sessionStatus, format, maxPlayers, rumbleMode, connectionType, roomCode, currentTurn, players]);

    // Auto-save on every state change
    useEffect(() => {
        saveSessionState();
    }, [saveSessionState]);

    // Restore session state on mount
    useEffect(() => {
        const saved = localStorage.getItem(SESSION_STORAGE_KEY);
        if (saved) {
            try {
                const state = JSON.parse(saved);
                // Only restore if saved within the last 24 hours
                if (state.savedAt && Date.now() - state.savedAt < 24 * 60 * 60 * 1000) {
                    setFormat(state.format || 'commander');
                    setMaxPlayers(state.maxPlayers || 4);
                    setRumbleMode(state.rumbleMode || false);
                    setFormation(state.formation || getDefaultFormation(state.maxPlayers || 4));
                    setConnectionType(state.connectionType || 'local');
                    setRoomCode(state.roomCode || '');
                    setCurrentTurn(state.currentTurn || 0);
                    setSessionStatus(state.sessionStatus || 'LOBBY');
                    if (state.players && state.players.length > 0) {
                        setPlayers(state.players.map((p: any) => ({
                            ...p,
                            means: p.means || 'lifeTracker',
                            features: p.features || { hasMic: false, handRevealed: false },
                        })));
                    }
                    setAppState('IN_SESSION');
                    console.log('🔄 Restored session state from crash protection');
                } else {
                    localStorage.removeItem(SESSION_STORAGE_KEY);
                }
            } catch (err) {
                console.error('Failed to restore session state:', err);
                localStorage.removeItem(SESSION_STORAGE_KEY);
            }
        }
    }, []);

    // Clear saved state when leaving session
    const clearSavedSession = useCallback(() => {
        localStorage.removeItem(SESSION_STORAGE_KEY);
    }, []);

    // Check for deck data from DISCOVER tab
    useEffect(() => {
        const storedDeck = localStorage.getItem('deckstr_session_deck');
        if (storedDeck) {
            try {
                const deckData = JSON.parse(storedDeck);
                // Load deck into Player 1 and enable Playtester mode
                setPlayers(prev => prev.map((p, idx) =>
                    idx === 0
                        ? {
                            ...p,
                            deck: deckData.cards,
                            deckName: deckData.deckName,
                            means: 'playtester' as PlayerMeans,
                        }
                        : p
                ));
                // Clear the stored deck so it doesn't reload on next mount
                localStorage.removeItem('deckstr_session_deck');
                // Auto-start session if we have a deck
                setAppState('IN_SESSION');
                console.log('📦 Loaded deck from DISCOVER:', deckData.deckName);
            } catch (err) {
                console.error('Failed to parse stored deck:', err);
            }
        }
    }, []);

    const handleCreateRoom = async () => {
        if (connectionType !== 'online') return;
        setIsConnecting(true);
        try {
            // TODO: Connect to sessionP2P.createRoom()
            const newRoomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
            setRoomCode(newRoomCode);
            console.log('🏠 Created room:', newRoomCode);
        } catch (err) {
            console.error('Failed to create room:', err);
        } finally {
            setIsConnecting(false);
        }
    };

    const handleJoinRoom = async () => {
        if (!joinRoomCode.trim() || joinRoomCode.length < 4) return;

        // Store the code and show mode selection modal
        setPendingJoinCode(joinRoomCode.toUpperCase());
        setShowJoinModeModal(true);
    };

    // Called after user selects mode in join modal
    const handleConfirmJoin = async () => {
        if (!pendingJoinCode || !joinPlayerName.trim()) return;

        setIsConnecting(true);
        try {
            // TODO: Connect to sessionService.joinSession()
            setRoomCode(pendingJoinCode);
            setShowJoinModeModal(false);
            setJoinRoomCode('');
            console.log('🚪 Joined room:', pendingJoinCode, 'as', joinPlayerName, 'means:', joinMeans);

            // TODO: Actually join and get session data, then transition to IN_SESSION
        } catch (err) {
            console.error('Failed to join room:', err);
        } finally {
            setIsConnecting(false);
        }
    };

    // Recording Handling
    const handleStartRecording = async () => {
        if (!recordSession) return;

        // Check consent - in real implementation, prompt players
        const allConsented = players.every(p => recordingConsent.get(p.id) === true);
        if (!allConsented && players.length > 1) {
            setShowConsentPrompt(true);
            return;
        }

        // TODO: Connect to sessionRecorder.startRecording()
        setIsRecording(true);
        console.log('🔴 Recording started');
    };

    const handleStopRecording = () => {
        // TODO: Connect to sessionRecorder.stopRecording()
        setIsRecording(false);
        console.log('⏹️ Recording stopped');
    };

    // Handle player consent for recording
    const handlePlayerConsent = (playerId: string, consented: boolean) => {
        setRecordingConsent(prev => {
            const newMap = new Map(prev);
            newMap.set(playerId, consented);
            return newMap;
        });
    };

    // Dice Roller
    const rollDie = useCallback((die: DieType) => {
        setIsRolling(true);
        // Animate for 600ms
        const animationInterval = setInterval(() => {
            const preview = die === 'coin'
                ? (Math.random() > 0.5 ? 1 : 2)
                : Math.floor(Math.random() * DIE_MAX[die]) + 1;
            setLastRoll({ die, result: preview, label: die === 'coin' ? (preview === 1 ? 'Heads' : 'Tails') : String(preview), timestamp: Date.now() });
        }, 50);

        setTimeout(() => {
            clearInterval(animationInterval);
            const finalResult = die === 'coin'
                ? (Math.random() > 0.5 ? 1 : 2)
                : Math.floor(Math.random() * DIE_MAX[die]) + 1;
            const roll: DiceRoll = {
                die,
                result: finalResult,
                label: die === 'coin' ? (finalResult === 1 ? 'Heads' : 'Tails') : String(finalResult),
                timestamp: Date.now(),
            };
            setLastRoll(roll);
            setDiceHistory(prev => [roll, ...prev].slice(0, 20));
            setIsRolling(false);
        }, 600);
    }, []);


    // View mode change handler
    const handleViewModeChange = useCallback((mode: ViewMode, seatIndex?: number) => {
        setViewMode(mode);
        if (mode === 'seat' && seatIndex !== undefined) {
            setFocusedSeat(seatIndex);
        } else if (mode === 'left') {
            const newSeat = (currentTurn - 1 + players.length) % players.length;
            setFocusedSeat(newSeat);
            setViewMode('seat');
        } else if (mode === 'right') {
            const newSeat = (currentTurn + 1) % players.length;
            setFocusedSeat(newSeat);
            setViewMode('seat');
        } else {
            setFocusedSeat(null);
        }
    }, [currentTurn, players.length]);

    // Enable keyboard shortcuts for layout switching
    useLayoutHotkeys(handleViewModeChange, players.length);

    // Broadcast session state to OBS overlay
    const broadcastSessionState = useCallback(() => {
        if (!roomCode) return;
        const channel = supabase.channel(`session-overlay-${roomCode}`);
        channel.send({
            type: 'broadcast',
            event: 'session-state',
            payload: {
                roomCode,
                format,
                status: sessionStatus,
                players: players.map(p => ({
                    id: p.id,
                    name: p.name,
                    seatIndex: p.seatIndex,
                    life: p.life,
                    poison: p.poison,
                    status: p.status,
                    features: p.features,
                }))
            }
        });
    }, [roomCode, format, sessionStatus, players]);

    // Broadcast life change event for OBS popup
    const broadcastLifeChange = useCallback((playerId: string, delta: number, newLife: number) => {
        if (!roomCode) return;
        const channel = supabase.channel(`session-overlay-${roomCode}`);
        channel.send({
            type: 'broadcast',
            event: 'life-change',
            payload: { playerId, delta, newLife }
        });
    }, [roomCode]);

    // Life adjustment
    const adjustLife = (playerId: string, delta: number) => {
        setPlayers(prev => {
            const updated = prev.map(p =>
                p.id === playerId ? { ...p, life: Math.max(0, p.life + delta) } : p
            );
            // Broadcast to OBS
            const player = updated.find(p => p.id === playerId);
            if (player) {
                broadcastLifeChange(playerId, delta, player.life);
            }
            return updated;
        });
    };

    // Poison adjustment
    const adjustPoison = (playerId: string, delta: number) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, poison: Math.max(0, Math.min(10, p.poison + delta)) } : p
        ));
    };

    // Commander tax adjustment
    const adjustCommanderTax = (playerId: string, delta: number) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, commanderTax: Math.max(0, p.commanderTax + delta) } : p
        ));
    };

    // Commander damage adjustment
    const adjustCommanderDamage = (playerId: string, fromSeatIndex: number, delta: number) => {
        setPlayers(prev => prev.map(p => {
            if (p.id !== playerId) return p;
            const newDamage = [...p.commanderDamage];
            newDamage[fromSeatIndex] = Math.max(0, (newDamage[fromSeatIndex] || 0) + delta);
            return { ...p, commanderDamage: newDamage };
        }));
    };

    // Update player features (each player chooses their own)
    const updatePlayerFeatures = (playerId: string, features: Partial<PlayerFeatures>) => {
        setPlayers(prev => prev.map(p => {
            if (p.id !== playerId) return p;
            return {
                ...p,
                features: { ...p.features, ...features },
                lastActiveAt: Date.now()
            };
        }));
    };

    // Update player means (switch between lifeTracker/playtester/webcam)
    const updatePlayerMeans = (playerId: string, means: PlayerMeans) => {
        setPlayers(prev => prev.map(p => {
            if (p.id !== playerId) return p;
            return { ...p, means, lastActiveAt: Date.now() };
        }));
    };

    // Extra counter adjustments
    const adjustExperience = (playerId: string, delta: number) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, experience: Math.max(0, p.experience + delta) } : p
        ));
    };

    const adjustEnergy = (playerId: string, delta: number) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, energy: Math.max(0, p.energy + delta) } : p
        ));
    };

    const adjustTreasure = (playerId: string, delta: number) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, treasure: Math.max(0, p.treasure + delta) } : p
        ));
    };

    // World state toggles
    const toggleMonarch = (playerId: string) => {
        setWorldState(prev => ({
            ...prev,
            monarch: prev.monarch === playerId ? null : playerId,
        }));
    };

    const toggleInitiative = (playerId: string) => {
        setWorldState(prev => ({
            ...prev,
            initiative: prev.initiative === playerId ? null : playerId,
        }));
    };

    const cycleDayNight = () => {
        setWorldState(prev => ({
            ...prev,
            dayNight: prev.dayNight === 'none' ? 'day' : prev.dayNight === 'day' ? 'night' : 'none',
        }));
    };

    const toggleCityBlessing = (playerId: string) => {
        setWorldState(prev => {
            const newSet = new Set(prev.cityBlessing);
            if (newSet.has(playerId)) {
                newSet.delete(playerId);
            } else {
                newSet.add(playerId);
            }
            return { ...prev, cityBlessing: newSet };
        });
    };

    // Phase stepper
    const advancePhase = () => {
        const idx = PHASES.findIndex(p => p.id === currentPhase);
        if (idx < PHASES.length - 1) {
            setCurrentPhase(PHASES[idx + 1].id);
        } else {
            // End of phases → advance turn and reset to untap
            setCurrentTurn((currentTurn + 1) % players.length);
            setCurrentPhase('untap');
        }
    };

    const previousPhase = () => {
        const idx = PHASES.findIndex(p => p.id === currentPhase);
        if (idx > 0) {
            setCurrentPhase(PHASES[idx - 1].id);
        }
    };

    // ====== TURN TIMER ======
    // Reset timer when turn changes or timer duration changes
    useEffect(() => {
        if (turnTimerDuration > 0 && sessionStatus === 'ACTIVE') {
            setTurnTimeRemaining(turnTimerDuration);
            setTimerActive(true);
        } else {
            setTimerActive(false);
        }
    }, [currentTurn, turnTimerDuration, sessionStatus]);

    // Countdown effect
    useEffect(() => {
        if (!timerActive || turnTimeRemaining <= 0) return;
        const interval = setInterval(() => {
            setTurnTimeRemaining(prev => {
                if (prev <= 1) {
                    setTimerActive(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [timerActive, turnTimeRemaining]);

    // Spacebar = Pass Turn
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code !== 'Space') return;
            // Don't fire if typing in an input/textarea
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (sessionStatus !== 'ACTIVE') return;
            e.preventDefault();
            setCurrentTurn(prev => (prev + 1) % players.length);
            setCurrentPhase('untap');
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [sessionStatus, players.length]);

    // Format timer display
    const formatTimer = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // ====== PLAYER PROFILES ======
    const loadPlayerProfiles = useCallback((): PlayerProfile[] => {
        try {
            const saved = localStorage.getItem(PLAYER_PROFILES_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    }, []);

    const savePlayerProfiles = useCallback((profiles: PlayerProfile[]) => {
        try {
            localStorage.setItem(PLAYER_PROFILES_KEY, JSON.stringify(profiles));
        } catch (err) {
            console.error('Failed to save player profiles:', err);
        }
    }, []);

    // Save current player names/means as profiles
    const saveCurrentAsProfiles = useCallback(() => {
        const profiles: PlayerProfile[] = players.map(p => ({
            name: p.name,
            preferredMeans: p.means,
            commanderName: p.commanderName,
            commanderArt: p.commanderArt,
        }));
        savePlayerProfiles(profiles);
    }, [players, savePlayerProfiles]);

    // Apply saved profiles to players (on session create)
    const applyProfilesToPlayers = useCallback((playerList: Player[]): Player[] => {
        const profiles = loadPlayerProfiles();
        return playerList.map((p, i) => {
            if (i < profiles.length) {
                return {
                    ...p,
                    name: profiles[i].name || p.name,
                    means: profiles[i].preferredMeans || p.means,
                    commanderName: profiles[i].commanderName || '',
                    commanderArt: profiles[i].commanderArt || '',
                };
            }
            return p;
        });
    }, [loadPlayerProfiles]);

    // ====== RANDOM SEATING ======
    const shuffleSeating = useCallback(() => {
        setPlayers(prev => {
            const shuffled = [...prev];
            // Fisher-Yates shuffle
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            // Reassign seat indices
            return shuffled.map((p, idx) => ({ ...p, seatIndex: idx }));
        });
    }, []);

    // ====== COMMANDER NAME UPDATE ======
    const commanderLookupTimers = useRef<Record<string, NodeJS.Timeout>>({});

    const updateCommanderName = useCallback((playerId: string, name: string) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, commanderName: name } : p
        ));

        // Debounced Scryfall art fetch
        if (commanderLookupTimers.current[playerId]) {
            clearTimeout(commanderLookupTimers.current[playerId]);
        }
        if (name.trim().length >= 3) {
            commanderLookupTimers.current[playerId] = setTimeout(async () => {
                try {
                    const res = await fetch(`https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(name.trim())}`);
                    if (res.ok) {
                        const data = await res.json();
                        const artCrop = data.image_uris?.art_crop || data.card_faces?.[0]?.image_uris?.art_crop || '';
                        if (artCrop) {
                            setPlayers(prev => prev.map(p =>
                                p.id === playerId ? { ...p, commanderArt: artCrop } : p
                            ));
                        }
                    }
                } catch { /* ignore fetch errors */ }
            }, 600);
        }
    }, []);

    const updateCommanderArt = useCallback((playerId: string, artUrl: string) => {
        setPlayers(prev => prev.map(p =>
            p.id === playerId ? { ...p, commanderArt: artUrl } : p
        ));
    }, []);

    // Add player (for local testing)
    const addPlayer = () => {
        if (players.length >= 8) return;
        const profiles = loadPlayerProfiles();
        const newIdx = players.length;
        const profile = newIdx < profiles.length ? profiles[newIdx] : null;
        const newPlayer: Player = {
            id: String(Date.now()),
            name: profile?.name || `Player ${newIdx + 1}`,
            seatIndex: newIdx,
            life: FORMAT_PRESETS[format].startingLife,
            poison: 0,
            commanderTax: 0,
            commanderDamage: [0, 0, 0, 0, 0, 0, 0, 0],
            status: 'active',
            isReady: false,
            hasRolled: false,
            rollValue: null,
            means: profile?.preferredMeans || (connectionType === 'local' ? 'lifeTracker' : 'webcam'),
            features: defaultFeatures,
            lastActiveAt: Date.now(),
            experience: 0,
            energy: 0,
            treasure: 0,
            commanderName: profile?.commanderName || '',
            commanderArt: profile?.commanderArt || '',
        };
        setPlayers(prev => [...prev, newPlayer]);
        setFormation(getDefaultFormation(players.length + 1));
        saveSessionState();
    };

    // Remove player
    const removePlayer = (playerId: string) => {
        if (players.length <= 2) return;
        setPlayers(prev => prev.filter(p => p.id !== playerId));
        setFormation(getDefaultFormation(players.length - 1));
    };

    // ============ LANDING VIEW ============
    if (appState === 'LANDING') {
        return (
            <div className="h-full flex flex-col overflow-hidden">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6">

                    {/* Header */}
                    <div className="text-center py-2">
                        <h1 className="text-2xl font-bold text-white">SESSION</h1>
                        <p className="text-white/60 text-sm">Multiplayer life tracking & tabletop companion</p>
                    </div>

                    {/* My Sessions Section */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-purple-300 flex items-center gap-2">
                                <Users className="size-5" />
                                My Sessions
                            </h2>
                            <span className="text-xs text-white/40">0 / 5 active</span>
                        </div>

                        {/* Empty state */}
                        <div className="text-center py-6 text-white/40">
                            <p className="text-sm">No active sessions</p>
                            <p className="text-xs">Host a session to get started</p>
                        </div>

                        {/* TODO: List of user's sessions */}
                    </div>

                    {/* Public Sessions Section */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-cyan-300 flex items-center gap-2">
                                <Wifi className="size-5" />
                                Public Sessions
                            </h2>
                            <button className="text-xs text-cyan-400 hover:text-cyan-300">Refresh</button>
                        </div>

                        {/* Join by code */}
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                placeholder="Enter room code..."
                                value={joinRoomCode}
                                onChange={(e) => setJoinRoomCode(e.target.value.toUpperCase())}
                                className="flex-1 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 font-mono uppercase tracking-widest text-sm"
                                maxLength={6}
                            />
                            <button
                                onClick={handleJoinRoom}
                                disabled={isConnecting || joinRoomCode.length < 4}
                                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                            >
                                Join
                            </button>
                        </div>

                        {/* Public rooms list - empty state */}
                        <div className="text-center py-4 text-white/40">
                            <p className="text-sm">No public sessions available</p>
                            <p className="text-xs">Be the first to host!</p>
                        </div>

                        {/* TODO: List of public sessions */}
                    </div>
                </div>

                {/* Fixed Host Button at Bottom */}
                <div className="flex-shrink-0 p-4 border-t border-white/10 bg-slate-900/80 backdrop-blur">
                    <button
                        onClick={() => setShowHostModal(true)}
                        className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-500 hover:to-amber-400 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
                    >
                        <Play className="size-5" />
                        Host a Session
                    </button>
                </div>

                {/* Host Session Modal */}
                {showHostModal && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-4 flex items-center justify-between">
                                <h2 className="text-lg font-bold text-white">Host a Session</h2>
                                <button
                                    onClick={() => setShowHostModal(false)}
                                    className="text-white/60 hover:text-white"
                                >✕</button>
                            </div>

                            <div className="p-4 space-y-4">
                                {/* Format Selection */}
                                <div className="space-y-2">
                                    <label className="text-white/60 text-sm">Format</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {Object.entries(FORMAT_PRESETS).map(([key, preset]) => (
                                            <button
                                                key={key}
                                                onClick={() => setFormat(key as keyof typeof FORMAT_PRESETS)}
                                                className={`px-2 py-2 rounded-lg text-xs transition-all ${format === key
                                                    ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300'
                                                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                    }`}
                                            >
                                                {preset.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Player Count */}
                                <div className="space-y-2">
                                    <label className="text-white/60 text-sm">Players</label>
                                    <div className="flex gap-1">
                                        {[2, 3, 4, 5, 6, 7, 8].map(n => (
                                            <button
                                                key={n}
                                                onClick={() => { setMaxPlayers(n); setFormation(getDefaultFormation(n)); }}
                                                className={`flex-1 py-2 rounded-lg text-sm transition-all ${maxPlayers === n
                                                    ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300'
                                                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                    }`}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Formation Layout */}
                                {maxPlayers > 1 && (
                                    <div className="space-y-2">
                                        <label className="text-white/60 text-sm">Layout</label>
                                        <FormationPicker
                                            playerCount={maxPlayers}
                                            selectedFormation={formation}
                                            onSelect={setFormation}
                                        />
                                    </div>
                                )}

                                {/* Connection Type */}
                                <div className="space-y-2">
                                    <label className="text-white/60 text-sm">Connection</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={() => setConnectionType('local')}
                                            className={`px-3 py-3 rounded-lg text-sm transition-all flex items-center justify-center gap-2 ${connectionType === 'local'
                                                ? 'bg-green-500/30 border border-green-500/50 text-green-300'
                                                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                }`}
                                        >
                                            <WifiOff className="size-4" />
                                            Local
                                        </button>
                                        <button
                                            onClick={() => setConnectionType('online')}
                                            className={`px-3 py-3 rounded-lg text-sm transition-all flex items-center justify-center gap-2 ${connectionType === 'online'
                                                ? 'bg-cyan-500/30 border border-cyan-500/50 text-cyan-300'
                                                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                }`}
                                        >
                                            <Wifi className="size-4" />
                                            Online P2P
                                        </button>
                                    </div>
                                </div>

                                {/* Public/Private Toggle (Online only) */}
                                {connectionType === 'online' && (
                                    <div className="space-y-2">
                                        <label className="text-white/60 text-sm">Visibility</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                onClick={() => setIsPublicSession(true)}
                                                className={`px-3 py-3 rounded-lg text-sm transition-all flex items-center justify-center gap-2 ${isPublicSession
                                                    ? 'bg-cyan-500/30 border border-cyan-500/50 text-cyan-300'
                                                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                    }`}
                                            >
                                                🌐 Public
                                            </button>
                                            <button
                                                onClick={() => setIsPublicSession(false)}
                                                className={`px-3 py-3 rounded-lg text-sm transition-all flex items-center justify-center gap-2 ${!isPublicSession
                                                    ? 'bg-amber-500/30 border border-amber-500/50 text-amber-300'
                                                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                    }`}
                                            >
                                                🔒 Private
                                            </button>
                                        </div>
                                        <p className="text-xs text-white/40">
                                            {isPublicSession
                                                ? 'Anyone can find and join your session'
                                                : 'Only players with the room code can join'}
                                        </p>

                                        {/* Games Total (for public sessions) */}
                                        {isPublicSession && (
                                            <div className="mt-3">
                                                <label className="text-white/60 text-xs block mb-1">Best of</label>
                                                <div className="flex gap-1">
                                                    {[1, 3, 5].map(n => (
                                                        <button
                                                            key={n}
                                                            onClick={() => setGamesTotal(n)}
                                                            className={`flex-1 py-1 rounded text-xs transition-all ${gamesTotal === n
                                                                ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300'
                                                                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                                }`}
                                                        >
                                                            {n}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Note about means */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                                    <p className="text-xs text-white/50">
                                        📌 Each player picks their own mode—Life Tracker, Playtester, or Webcam—when they join.
                                    </p>
                                </div>

                                {/* Options Row */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setRumbleMode(!rumbleMode)}
                                        className={`flex-1 px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1 ${rumbleMode
                                            ? 'bg-amber-500/30 border border-amber-500/50 text-amber-300'
                                            : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                            }`}
                                    >
                                        <Swords className="size-3" />
                                        Rumble
                                    </button>
                                    <button
                                        onClick={() => setRecordSession(!recordSession)}
                                        className={`flex-1 px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1 ${recordSession
                                            ? 'bg-red-500/30 border border-red-500/50 text-red-300'
                                            : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                            }`}
                                    >
                                        <Circle className={`size-3 ${recordSession ? 'fill-red-400' : ''}`} />
                                        Record
                                    </button>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-slate-900 border-t border-white/10 p-4">
                                <button
                                    onClick={async () => {
                                        if (connectionType === 'online') {
                                            await handleCreateRoom();
                                        }
                                        let initialPlayers: Player[] = Array.from({ length: maxPlayers }, (_, i) => ({
                                            id: String(i + 1),
                                            name: `Player ${i + 1}`,
                                            seatIndex: i,
                                            life: FORMAT_PRESETS[format].startingLife,
                                            poison: 0,
                                            commanderTax: 0,
                                            commanderDamage: [0, 0, 0, 0, 0, 0, 0, 0],
                                            status: 'active' as const,
                                            isReady: false,
                                            hasRolled: false,
                                            rollValue: null,
                                            means: (connectionType === 'local' ? 'lifeTracker' : 'webcam') as PlayerMeans,
                                            features: defaultFeatures,
                                            lastActiveAt: Date.now(),
                                            experience: 0,
                                            energy: 0,
                                            treasure: 0,
                                            commanderName: '',
                                            commanderArt: '',
                                        }));
                                        // Apply saved profiles (names, means, commander) if available
                                        initialPlayers = applyProfilesToPlayers(initialPlayers);
                                        setPlayers(initialPlayers);
                                        setShowHostModal(false);
                                        setAppState('IN_SESSION');
                                        setSessionStatus('LOBBY');
                                    }}
                                    disabled={isConnecting}
                                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-500 hover:to-amber-400 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isConnecting ? '⏳ Creating...' : (
                                        <>
                                            <Play className="size-5" />
                                            Start Session
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Join Mode Selection Modal */}
                {showJoinModeModal && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-sm">
                            {/* Modal Header */}
                            <div className="border-b border-white/10 p-4 flex items-center justify-between">
                                <h2 className="text-lg font-bold text-white">Join Session</h2>
                                <button
                                    onClick={() => {
                                        setShowJoinModeModal(false);
                                        setPendingJoinCode('');
                                    }}
                                    className="text-white/60 hover:text-white"
                                >✕</button>
                            </div>

                            <div className="p-4 space-y-4">
                                {/* Room Code Display */}
                                <div className="text-center p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                                    <p className="text-cyan-300/60 text-xs">Joining Room</p>
                                    <p className="text-2xl font-mono font-bold text-cyan-300 tracking-widest">{pendingJoinCode}</p>
                                </div>

                                {/* Player Name */}
                                <div className="space-y-2">
                                    <label className="text-white/60 text-sm">Your Name</label>
                                    <input
                                        type="text"
                                        value={joinPlayerName}
                                        onChange={(e) => setJoinPlayerName(e.target.value)}
                                        placeholder="Enter your name..."
                                        className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50"
                                        maxLength={20}
                                    />
                                </div>

                                {/* Means Selection */}
                                <div className="space-y-2">
                                    <label className="text-white/60 text-sm">How will you play?</label>
                                    <div className="space-y-2">
                                        {MEANS_BY_ENV[connectionType].map(m => (
                                            <button
                                                key={m}
                                                onClick={() => setJoinMeans(m)}
                                                className={`w-full px-3 py-3 rounded-lg text-sm transition-all flex items-center gap-3 ${joinMeans === m
                                                    ? m === 'playtester'
                                                        ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300'
                                                        : m === 'webcam'
                                                            ? 'bg-cyan-500/30 border border-cyan-500/50 text-cyan-300'
                                                            : 'bg-amber-500/30 border border-amber-500/50 text-amber-300'
                                                    : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                                                    }`}
                                            >
                                                <span className="text-xl">{MEANS_META[m].icon}</span>
                                                <div className="flex-1 text-left">
                                                    <p className="font-medium">{MEANS_META[m].label}</p>
                                                    <p className="text-[10px] text-white/40">{MEANS_META[m].description}</p>
                                                </div>
                                                <span className="text-xs">{joinMeans === m ? '✓' : ''}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Mic Toggle (online only) */}
                                    {connectionType === 'online' && (
                                        <button
                                            onClick={() => setJoinMic(!joinMic)}
                                            className={`w-full px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 mt-2 ${joinMic
                                                ? 'bg-green-500/20 border border-green-500/40 text-green-300'
                                                : 'bg-white/5 border border-white/10 text-white/50 hover:bg-white/10'
                                                }`}
                                        >
                                            <Activity className="size-4" />
                                            <span>Microphone</span>
                                            <span className="ml-auto">{joinMic ? 'ON' : 'OFF'}</span>
                                        </button>
                                    )}
                                </div>
                            </div>


                            {/* Modal Footer */}
                            <div className="border-t border-white/10 p-4">
                                <button
                                    onClick={handleConfirmJoin}
                                    disabled={isConnecting || !joinPlayerName.trim()}
                                    className="w-full px-6 py-3 bg-gradient-to-r from-cyan-600 to-purple-500 hover:from-cyan-500 hover:to-purple-400 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isConnecting ? '⏳ Joining...' : '✓ Join Session'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ============ IN SESSION VIEW ============
    return (
        <div className="h-full flex flex-col p-1 gap-1 overflow-hidden">
            {/* ═══════════ SESSION HEADER — Minimal 3-zone bar ═══════════ */}
            <div className="flex items-center justify-between bg-black/40 backdrop-blur-sm rounded-lg px-3 py-1 flex-shrink-0 border border-white/5 relative z-50">
                {/* LEFT: Format + Status */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-white/70 font-medium text-xs">{FORMAT_PRESETS[format].name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${sessionStatus === 'LOBBY' ? 'bg-amber-500/20 text-amber-400' :
                        sessionStatus === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' :
                            sessionStatus === 'PAUSED' ? 'bg-orange-500/20 text-orange-400' :
                                'bg-white/10 text-white/50'
                        }`}>
                        {sessionStatus}
                    </span>
                    {rumbleMode && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/20 text-amber-400">
                            RUMBLE
                        </span>
                    )}
                </div>

                {/* CENTER: Turn indicator (active only) */}
                <div className="flex items-center gap-1">
                    {sessionStatus === 'ACTIVE' && (
                        <>
                            <button
                                onClick={() => setCurrentTurn((currentTurn - 1 + players.length) % players.length)}
                                className="p-1 rounded text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
                                title="Previous Turn"
                            >
                                <ChevronLeft className="size-3.5" />
                            </button>
                            <span className="text-xs text-white/40 select-none">
                                Turn: <span className="text-amber-400 font-bold">{players[currentTurn]?.name || '?'}</span>
                            </span>
                            <button
                                onClick={() => setCurrentTurn((currentTurn + 1) % players.length)}
                                className="p-1 rounded text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
                                title="Next Turn"
                            >
                                <ChevronRight className="size-3.5" />
                            </button>
                            <button
                                onClick={() => { setCurrentTurn((currentTurn + 1) % players.length); setCurrentPhase('untap'); }}
                                className="ml-1 px-2 py-0.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 rounded text-[10px] font-semibold transition-all border border-emerald-500/20"
                                title="Pass Turn (Spacebar)"
                            >
                                Pass
                            </button>
                        </>
                    )}
                    {sessionStatus === 'LOBBY' && (
                        <span className="text-xs text-white/30">Waiting to start...</span>
                    )}
                </div>

                {/* RIGHT: Icon-only tools */}
                <div className="flex items-center gap-0.5">
                    {/* Invite */}
                    <button
                        onClick={() => {
                            const code = roomCode || Math.random().toString(36).substring(2, 8).toUpperCase();
                            if (!roomCode) setRoomCode(code);
                            const url = `${window.location.origin}?session=${code}`;
                            navigator.clipboard.writeText(url).then(() => {
                                setInviteCopied(true);
                                setTimeout(() => setInviteCopied(false), 2000);
                            });
                        }}
                        className={`p-1.5 rounded transition-all ${inviteCopied
                            ? 'bg-green-500/20 text-green-400'
                            : 'text-white/30 hover:text-cyan-400 hover:bg-cyan-500/10'
                            }`}
                        title={inviteCopied ? 'Copied!' : 'Copy invite link'}
                    >
                        {inviteCopied ? <Check className="size-3.5" /> : <Link2 className="size-3.5" />}
                    </button>

                    {/* Start / Pause / Resume */}
                    {sessionStatus === 'LOBBY' && (
                        <button onClick={() => { saveCurrentAsProfiles(); setSessionStatus('ACTIVE'); }} className="p-1.5 rounded text-emerald-400/60 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all" title="Start Game">
                            <Play className="size-3.5" />
                        </button>
                    )}
                    {sessionStatus === 'ACTIVE' && (
                        <button onClick={() => setSessionStatus('PAUSED')} className="p-1.5 rounded text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition-all" title="Pause">
                            <Pause className="size-3.5" />
                        </button>
                    )}
                    {sessionStatus === 'PAUSED' && (
                        <button onClick={() => setSessionStatus('ACTIVE')} className="p-1.5 rounded text-emerald-400/60 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all" title="Resume">
                            <Play className="size-3.5" />
                        </button>
                    )}

                    {/* Dice */}
                    <button
                        onClick={() => setShowDiceRoller(!showDiceRoller)}
                        className={`p-1.5 rounded transition-all ${showDiceRoller ? 'bg-purple-500/20 text-purple-300' : 'text-white/30 hover:text-white/60 hover:bg-white/5'}`}
                        title="Dice Roller"
                    >
                        <Dices className="size-3.5" />
                    </button>

                    {/* Timer */}
                    <div className="relative group">
                        <button
                            className={`p-1.5 rounded transition-all flex items-center gap-1 ${turnTimerDuration > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'text-white/30 hover:text-white/60 hover:bg-white/5'}`}
                            title="Turn Timer"
                        >
                            <Timer className="size-3.5" />
                            {turnTimerDuration > 0 && timerActive && (
                                <span className={`font-mono text-[9px] font-bold ${turnTimeRemaining <= 10 ? 'text-red-400 animate-pulse'
                                    : turnTimeRemaining <= 30 ? 'text-amber-400'
                                        : 'text-emerald-400'
                                    }`}>
                                    {formatTimer(turnTimeRemaining)}
                                </span>
                            )}
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-white/10 rounded-lg p-1.5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[100px]">
                            <p className="text-[9px] text-white/40 uppercase tracking-wider px-2 py-1">Turn Timer</p>
                            {TIMER_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => setTurnTimerDuration(opt.value)}
                                    className={`w-full text-left px-2 py-1 rounded text-xs transition-all ${turnTimerDuration === opt.value
                                        ? 'bg-emerald-500/30 text-emerald-300'
                                        : 'text-white/60 hover:bg-white/10 hover:text-white/80'
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Settings Gear — opens dropdown panel with layout, formation, world state toggle etc. */}
                    <div className="relative">
                        <button
                            onClick={() => setShowSessionMenu(!showSessionMenu)}
                            className={`p-1.5 rounded transition-all ${showSessionMenu ? 'bg-white/10 text-white/80' : 'text-white/30 hover:text-white/60 hover:bg-white/5'}`}
                            title="Session Settings"
                        >
                            <Settings className="size-3.5" />
                        </button>

                        {/* ── Gear dropdown panel ── */}
                        {showSessionMenu && (
                            <div className="absolute right-0 top-full mt-1 bg-slate-800/95 backdrop-blur-lg border border-white/10 rounded-xl p-3 z-50 min-w-[260px] shadow-2xl shadow-black/50">
                                <p className="text-[9px] text-white/30 uppercase tracking-widest mb-2">Session Settings</p>

                                {/* View Mode */}
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] text-white/50">View</span>
                                    <LayoutSelector
                                        viewMode={viewMode}
                                        focusedSeat={focusedSeat}
                                        playerCount={players.length}
                                        currentTurnSeat={currentTurn}
                                        onViewModeChange={handleViewModeChange}
                                    />
                                </div>

                                {/* Formation */}
                                {viewMode === 'table' && (() => {
                                    const formations = FORMATIONS[players.length] || [];
                                    if (formations.length <= 1) return null;
                                    return (
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-[10px] text-white/50">Layout</span>
                                            <div className="flex items-center gap-1">
                                                {formations.map((f: Formation) => {
                                                    const sel = formation === f.id;
                                                    return (
                                                        <button
                                                            key={f.id}
                                                            onClick={() => setFormation(f.id)}
                                                            className={`p-1 rounded border transition-all ${sel
                                                                ? 'border-purple-500 bg-purple-500/20'
                                                                : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
                                                                }`}
                                                            title={f.label}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateAreas: f.gridTemplateAreas,
                                                                    gridTemplateColumns: f.gridTemplateColumns,
                                                                    gridTemplateRows: f.gridTemplateRows,
                                                                    width: '32px',
                                                                    height: '22px',
                                                                    gap: '1px',
                                                                }}
                                                            >
                                                                {Array.from({ length: f.playerCount }, (_, i) => (
                                                                    <div
                                                                        key={i}
                                                                        style={{ gridArea: `s${i}` }}
                                                                        className={`rounded-[1px] ${sel ? 'bg-purple-400/60' : 'bg-white/20'}`}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Players (Lobby only) */}
                                {sessionStatus === 'LOBBY' && (
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] text-white/50">Players</span>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => removePlayer(players[players.length - 1]?.id)}
                                                disabled={players.length <= 2}
                                                className="p-1 rounded text-red-400/60 hover:bg-red-500/20 hover:text-red-400 disabled:opacity-30 transition-all"
                                                title="Remove Player"
                                            >
                                                <UserMinus className="size-3" />
                                            </button>
                                            <span className="text-white/50 text-xs font-mono">{players.length}</span>
                                            <button
                                                onClick={addPlayer}
                                                disabled={players.length >= 8}
                                                className="p-1 rounded text-purple-400/60 hover:bg-purple-500/20 hover:text-purple-300 disabled:opacity-30 transition-all"
                                                title="Add Player"
                                            >
                                                <UserPlus className="size-3" />
                                            </button>
                                            <div className="w-px h-4 bg-white/10 mx-0.5" />
                                            <button
                                                onClick={shuffleSeating}
                                                className="p-1 rounded text-white/30 hover:bg-white/10 hover:text-white/60 transition-all"
                                                title="Randomize Seating"
                                            >
                                                <ShuffleIcon className="size-3" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* World State Toggle */}
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] text-white/50">Phases & World State</span>
                                    <button
                                        onClick={() => setShowWorldState(!showWorldState)}
                                        className={`px-2 py-0.5 rounded text-[9px] font-medium transition-all ${showWorldState
                                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                            : 'bg-white/5 text-white/30 border border-white/10 hover:bg-white/10'
                                            }`}
                                    >
                                        {showWorldState ? 'ON' : 'OFF'}
                                    </button>
                                </div>

                                {/* Divider */}
                                <div className="h-px bg-white/5 my-2" />

                                {/* Reset */}
                                <button
                                    onClick={() => {
                                        setSessionStatus('LOBBY');
                                        setCurrentPhase('untap');
                                        setWorldState({ monarch: null, initiative: null, dayNight: 'none', cityBlessing: new Set() });
                                        setPlayers(prev => prev.map(p => ({
                                            ...p,
                                            life: FORMAT_PRESETS[format].startingLife,
                                            poison: 0,
                                            commanderTax: 0,
                                            commanderDamage: [0, 0, 0, 0, 0, 0, 0, 0],
                                            status: 'active' as const,
                                            isReady: false,
                                            hasRolled: false,
                                            rollValue: null,
                                            experience: 0,
                                            energy: 0,
                                            treasure: 0,
                                        })));
                                        setShowSessionMenu(false);
                                    }}
                                    className="w-full text-left px-2 py-1.5 rounded text-xs text-red-400/60 hover:bg-red-500/10 hover:text-red-400 flex items-center gap-2 transition-all"
                                >
                                    <RotateCcw className="size-3" /> Reset Game
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-4 bg-white/10 mx-0.5" />

                    {/* Leave */}
                    <button
                        onClick={() => { media.stopStream(); clearSavedSession(); setAppState('LANDING'); }}
                        className="p-1.5 rounded text-red-400/40 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        title="Leave Session"
                    >
                        <X className="size-3.5" />
                    </button>
                </div>
            </div>

            {/* ═══════════ WORLD STATE BAR — Hidden by default, toggleable ═══════════ */}
            {showWorldState && sessionStatus === 'ACTIVE' && (
                <div className="flex items-center justify-center gap-3 flex-shrink-0 bg-black/30 rounded-lg border border-white/5 px-3 py-1 transition-all">
                    {/* Phase Stepper */}
                    <div className="flex items-center gap-0.5">
                        <button
                            onClick={previousPhase}
                            className="text-white/30 hover:text-white/60 text-xs px-1 transition-colors"
                            title="Previous Phase"
                        >
                            ‹
                        </button>
                        {PHASES.map((phase, i) => (
                            <button
                                key={phase.id}
                                onClick={() => setCurrentPhase(phase.id)}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-all ${currentPhase === phase.id
                                    ? 'bg-amber-500/30 text-amber-300 border border-amber-500/40'
                                    : PHASES.findIndex(p => p.id === currentPhase) > i
                                        ? 'text-white/20 bg-white/5'
                                        : 'text-white/40 hover:bg-white/10'
                                    }`}
                                title={phase.label}
                            >
                                {phase.short}
                            </button>
                        ))}
                        <button
                            onClick={advancePhase}
                            className="text-amber-400 hover:text-amber-300 text-xs px-1 transition-colors"
                            title="Next Phase"
                        >
                            <ChevronRight className="size-3" />
                        </button>
                    </div>

                    <div className="w-px h-4 bg-white/10" />

                    {/* Monarch */}
                    <div className="flex items-center gap-1">
                        <Crown className={`size-3 ${worldState.monarch ? 'text-amber-400' : 'text-white/15'}`} />
                        <div className="flex gap-0.5">
                            {players.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => toggleMonarch(p.id)}
                                    className={`w-3.5 h-3.5 rounded-full text-[7px] font-bold flex items-center justify-center transition-all ${worldState.monarch === p.id
                                        ? 'bg-amber-400 text-black'
                                        : 'bg-white/10 text-white/30 hover:bg-white/20'
                                        }`}
                                    title={`${p.name}: ${worldState.monarch === p.id ? 'Remove Monarch' : 'Make Monarch'}`}
                                >
                                    {p.seatIndex + 1}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Initiative */}
                    <div className="flex items-center gap-1">
                        <Shield className={`size-3 ${worldState.initiative ? 'text-cyan-400' : 'text-white/15'}`} />
                        <div className="flex gap-0.5">
                            {players.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => toggleInitiative(p.id)}
                                    className={`w-3.5 h-3.5 rounded-full text-[7px] font-bold flex items-center justify-center transition-all ${worldState.initiative === p.id
                                        ? 'bg-cyan-400 text-black'
                                        : 'bg-white/10 text-white/30 hover:bg-white/20'
                                        }`}
                                    title={`${p.name}: ${worldState.initiative === p.id ? 'Remove Initiative' : 'Give Initiative'}`}
                                >
                                    {p.seatIndex + 1}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Day/Night */}
                    <button
                        onClick={cycleDayNight}
                        className={`p-1 rounded transition-all ${worldState.dayNight === 'day'
                            ? 'bg-amber-500/20 text-amber-300'
                            : worldState.dayNight === 'night'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'text-white/15 hover:bg-white/5 hover:text-white/30'
                            }`}
                        title={`Day/Night: ${worldState.dayNight === 'none' ? 'Off' : worldState.dayNight}`}
                    >
                        {worldState.dayNight === 'night'
                            ? <Moon className="size-3" />
                            : <Sun className="size-3" />
                        }
                    </button>

                    {/* City's Blessing */}
                    <div className="flex items-center gap-1">
                        <Sparkles className={`size-3 ${worldState.cityBlessing.size > 0 ? 'text-purple-400' : 'text-white/15'}`} />
                        <div className="flex gap-0.5">
                            {players.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => toggleCityBlessing(p.id)}
                                    className={`w-3.5 h-3.5 rounded-full text-[7px] font-bold flex items-center justify-center transition-all ${worldState.cityBlessing.has(p.id)
                                        ? 'bg-purple-400 text-black'
                                        : 'bg-white/10 text-white/30 hover:bg-white/20'
                                        }`}
                                    title={`${p.name}: ${worldState.cityBlessing.has(p.id) ? 'Remove City\'s Blessing' : 'Give City\'s Blessing'}`}
                                >
                                    {p.seatIndex + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            {/* Player Grid — fills viewport, no scroll */}
            <div
                className={`flex-1 grid gap-0 min-h-0 overflow-hidden ${viewMode !== 'table' ? 'grid-cols-1' : ''}`}
                style={viewMode === 'table' ? (() => {
                    const f = getFormationById(formation);
                    return f ? {
                        gridTemplateAreas: f.gridTemplateAreas,
                        gridTemplateColumns: f.gridTemplateColumns,
                        gridTemplateRows: f.gridTemplateRows,
                    } : {};
                })() : undefined}
            >
                {/* Render based on view mode */}
                {viewMode === 'table' ? (
                    // Table view: show all players
                    players.map(player => (
                        <div key={player.id} style={{ gridArea: `s${player.seatIndex}` }} className="min-h-0 overflow-hidden">
                            <PlayerContainer
                                player={player}
                                isCurrentTurn={player.seatIndex === currentTurn}
                                isFocused={focusedSeat === player.seatIndex}
                                onLifeChange={(delta) => adjustLife(player.id, delta)}
                                onPoisonChange={(delta) => adjustPoison(player.id, delta)}
                                onCommanderTaxChange={(delta) => adjustCommanderTax(player.id, delta)}
                                onCommanderDamageChange={(fromIdx, delta) => adjustCommanderDamage(player.id, fromIdx, delta)}
                                onFeaturesChange={(features) => updatePlayerFeatures(player.id, features)}
                                onMeansChange={(m) => updatePlayerMeans(player.id, m)}
                                showCommanderDamage={FORMAT_PRESETS[format].commanderDamage}
                                otherPlayers={players.filter(p => p.id !== player.id)}
                                compact={players.length > 4}
                                isOnline={connectionType === 'online'}
                                onExperienceChange={(delta) => adjustExperience(player.id, delta)}
                                onEnergyChange={(delta) => adjustEnergy(player.id, delta)}
                                onTreasureChange={(delta) => adjustTreasure(player.id, delta)}
                                isMonarch={worldState.monarch === player.id}
                                hasInitiative={worldState.initiative === player.id}
                                hasCityBlessing={worldState.cityBlessing.has(player.id)}
                                turnTimeRemaining={player.seatIndex === currentTurn && turnTimerDuration > 0 ? turnTimeRemaining : undefined}
                                isLobby={sessionStatus === 'LOBBY'}
                                onNameChange={(name) => setPlayers(prev => prev.map(pl => pl.id === player.id ? { ...pl, name } : pl))}
                                onCommanderNameChange={(name) => updateCommanderName(player.id, name)}
                                availableMeans={MEANS_BY_ENV[connectionType]}
                                showCommanderField={FORMAT_PRESETS[format].commanderDamage}
                                onDeckImported={(deck, deckName) => setPlayers(prev => prev.map(pl => pl.id === player.id ? { ...pl, deck, deckName: deckName || pl.deckName } : pl))}
                                onClearDeck={() => setPlayers(prev => prev.map(pl => pl.id === player.id ? { ...pl, deck: undefined, deckName: undefined } : pl))}
                                // Webcam props — in local mode, all players share the single camera
                                mediaStream={(connectionType === 'local' || player.seatIndex === localPlayerSeat) ? media.stream : null}
                                onToggleVideo={(connectionType === 'local' || player.seatIndex === localPlayerSeat) ? media.toggleVideo : undefined}
                                onToggleAudio={(connectionType === 'local' || player.seatIndex === localPlayerSeat) ? media.toggleAudio : undefined}
                                onRequestCamera={(connectionType === 'local' || player.seatIndex === localPlayerSeat) ? handleRequestCamera : undefined}
                                onSwitchCamera={(connectionType === 'local' || player.seatIndex === localPlayerSeat) && media.devices.videoInputs.length > 1 ? handleSwitchCamera : undefined}
                                videoEnabled={media.settings.videoEnabled}
                                audioEnabled={media.settings.audioEnabled}
                                isLocalPlayer={connectionType === 'local' || player.seatIndex === localPlayerSeat}
                            />
                        </div>
                    ))
                ) : (
                    // Seat view: focus on specific seat
                    focusedSeat !== null && players.find(p => p.seatIndex === focusedSeat) && (
                        <PlayerContainer
                            key={players.find(p => p.seatIndex === focusedSeat)!.id}
                            player={players.find(p => p.seatIndex === focusedSeat)!}
                            isCurrentTurn={focusedSeat === currentTurn}
                            isFocused={true}
                            onLifeChange={(delta) => adjustLife(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            onPoisonChange={(delta) => adjustPoison(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            onCommanderTaxChange={(delta) => adjustCommanderTax(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            onCommanderDamageChange={(fromIdx, delta) => adjustCommanderDamage(players.find(p => p.seatIndex === focusedSeat)!.id, fromIdx, delta)}
                            onFeaturesChange={(features) => updatePlayerFeatures(players.find(p => p.seatIndex === focusedSeat)!.id, features)}
                            showCommanderDamage={FORMAT_PRESETS[format].commanderDamage}
                            otherPlayers={players.filter(p => p.seatIndex !== focusedSeat)}
                            isOnline={connectionType === 'online'}
                            onExperienceChange={(delta) => adjustExperience(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            onEnergyChange={(delta) => adjustEnergy(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            onTreasureChange={(delta) => adjustTreasure(players.find(p => p.seatIndex === focusedSeat)!.id, delta)}
                            isMonarch={worldState.monarch === players.find(p => p.seatIndex === focusedSeat)!.id}
                            hasInitiative={worldState.initiative === players.find(p => p.seatIndex === focusedSeat)!.id}
                            hasCityBlessing={worldState.cityBlessing.has(players.find(p => p.seatIndex === focusedSeat)!.id)}
                            turnTimeRemaining={focusedSeat === currentTurn && turnTimerDuration > 0 ? turnTimeRemaining : undefined}
                            // Webcam props — in local mode, all players share the single camera
                            mediaStream={(connectionType === 'local' || focusedSeat === localPlayerSeat) ? media.stream : null}
                            onToggleVideo={(connectionType === 'local' || focusedSeat === localPlayerSeat) ? media.toggleVideo : undefined}
                            onToggleAudio={(connectionType === 'local' || focusedSeat === localPlayerSeat) ? media.toggleAudio : undefined}
                            onRequestCamera={(connectionType === 'local' || focusedSeat === localPlayerSeat) ? handleRequestCamera : undefined}
                            onSwitchCamera={(connectionType === 'local' || focusedSeat === localPlayerSeat) && media.devices.videoInputs.length > 1 ? handleSwitchCamera : undefined}
                            videoEnabled={media.settings.videoEnabled}
                            audioEnabled={media.settings.audioEnabled}
                            isLocalPlayer={connectionType === 'local' || focusedSeat === localPlayerSeat}
                        />
                    )
                )}
            </div>

            {/* Dice Roller Panel */}
            {
                showDiceRoller && (
                    <div className="flex-shrink-0 bg-white/5 rounded-lg border border-white/10 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-1">
                                <Dices className="size-3" /> Dice & Coins
                            </span>
                            {diceHistory.length > 0 && (
                                <button
                                    onClick={() => setDiceHistory([])}
                                    className="text-[10px] text-white/30 hover:text-white/50"
                                >
                                    Clear History
                                </button>
                            )}
                        </div>

                        {/* Die buttons */}
                        <div className="flex items-center gap-1.5 mb-2">
                            {(['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'coin'] as DieType[]).map(die => (
                                <button
                                    key={die}
                                    onClick={() => rollDie(die)}
                                    disabled={isRolling}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${isRolling
                                        ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                        : die === 'coin'
                                            ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30'
                                            : 'bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30'
                                        }`}
                                >
                                    {DIE_LABELS[die]}
                                </button>
                            ))}
                        </div>

                        {/* Last roll display */}
                        {lastRoll && (
                            <div className={`text-center py-2 rounded-lg mb-2 transition-all ${isRolling
                                ? 'bg-purple-500/20 border border-purple-500/40 animate-pulse'
                                : 'bg-gradient-to-r from-purple-500/10 to-amber-500/10 border border-white/10'
                                }`}>
                                <span className={`text-3xl font-black transition-all ${isRolling ? 'text-purple-300' :
                                    lastRoll.die === 'coin' ? 'text-amber-300' : 'text-white'
                                    }`}>
                                    {lastRoll.die === 'coin' ? (lastRoll.result === 1 ? '🪙 Heads' : '🪙 Tails') : lastRoll.label}
                                </span>
                                {!isRolling && (
                                    <span className="text-white/40 text-xs ml-2">
                                        ({lastRoll.die === 'coin' ? 'Coin Flip' : lastRoll.die})
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Roll history */}
                        {diceHistory.length > 1 && (
                            <div className="flex items-center gap-1 overflow-x-auto">
                                <History className="size-3 text-white/20 flex-shrink-0" />
                                {diceHistory.slice(1, 10).map((roll, i) => (
                                    <span
                                        key={roll.timestamp + i}
                                        className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono ${roll.die === 'coin'
                                            ? 'bg-amber-500/10 text-amber-400/60'
                                            : 'bg-purple-500/10 text-purple-400/60'
                                            }`}
                                        title={`${roll.die}: ${roll.label}`}
                                    >
                                        {roll.die === 'coin' ? (roll.result === 1 ? 'H' : 'T') : roll.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )
            }




        </div >
    );
}

export default SessionTab;
